import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/modules/auth/auth";
import {
  fetchPhotoUploadQuota,
  fetchShortUploadQuota,
  isQuotaExceeded,
} from "@/helpers/firestore/QuotaService";
import type { QuotaDocument } from "@kekkle/shared/types/documents/quota-document/quota-document";

export const useQuotaStore = defineStore("quota", () => {
  const authStore = useAuthStore();

  const photoQuota = ref<QuotaDocument | null>(null);
  const shortQuota = ref<QuotaDocument | null>(null);
  const loading = ref(false);

  const isPhotoQuotaExceeded = computed(() => {
    if (!photoQuota.value) return false;
    return isQuotaExceeded(photoQuota.value);
  });

  const isShortQuotaExceeded = computed(() => {
    if (!shortQuota.value) return false;
    return isQuotaExceeded(shortQuota.value);
  });

  async function fetchQuota(): Promise<void> {
    const uid = authStore.authUuid;
    if (!uid) return;

    loading.value = true;
    try {
      const [photo, short] = await Promise.all([
        fetchPhotoUploadQuota(uid),
        fetchShortUploadQuota(uid),
      ]);
      photoQuota.value = photo;
      shortQuota.value = short;
    } catch (error) {
      console.error("Failed to fetch quota:", error);
    } finally {
      loading.value = false;
    }
  }

  function reset(): void {
    photoQuota.value = null;
    shortQuota.value = null;
    loading.value = false;
  }

  return {
    photoQuota,
    shortQuota,
    loading,
    isPhotoQuotaExceeded,
    isShortQuotaExceeded,
    fetchQuota,
    reset,
  };
});
